import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useAddin } from '../App';
import MatterLookup from '../components/MatterLookup';
import * as webhook from '../services/webhook';
import { insertDraftBody, getComposeAttachments, addRecipient } from '../services/office';
import { MODELS, TONES } from '../utils/constants';

// Rough token estimator (~4 chars per token for English text)
const estimateTokens = (text) => Math.ceil((text || '').length / 4);

// Estimated token costs for context sources we can't measure client-side
const CONTEXT_SOURCE_ESTIMATES = {
    matterNotes: 600,
    recentActivity: 800,
    threadHistory: 1800,
    priorComms: 2500,
};
const PROMPT_OVERHEAD = 180; // fixed system prompt template in N8N

/**
 * Draft tab — AI model picker, tone selector, context injection toggles,
 * token meter, generated draft area, and action buttons.
 */
export default function DraftTab() {
    const { linkedMatter, setStatusLabel, userEmail, itemId, conversationId, userSettings, senderInfo, surface } = useAddin();
    const [selectedModel, setSelectedModel] = useState(userSettings?.defaultModel || 'claude-sonnet');

    // Fallback to constants.MODELS if userSettings hasn't initialized customModels
    const activeModels = userSettings?.customModels || MODELS;
    const [selectedTone, setSelectedTone] = useState('Formal');

    // ── Availability guards ──
    // A brand-new compose email has no conversationId — there is no existing thread to include.
    const isNewEmail = surface === 'compose' && !conversationId;
    // Matter-dependent sources require a linked matter.
    const hasMatter = !!linkedMatter;

    const [contextFlags, setContextFlags] = useState({
        threadHistory: !isNewEmail, // Off by default for brand-new emails
        matterNotes: false,
        recentActivity: false,
        priorComms: false,
    });
    const [threadLimit, setThreadLimit] = useState(2);

    // Notes fetching state
    const [availableNotes, setAvailableNotes] = useState([]);
    const [selectedNotes, setSelectedNotes] = useState({});
    const [loadingNotes, setLoadingNotes] = useState(false);
    const [notesError, setNotesError] = useState(null);

    const [customInstructions, setCustomInstructions] = useState('');
    const [draftText, setDraftText] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);

    // Auto-reset thread history flag if this becomes a new email
    useEffect(() => {
        if (isNewEmail) setContextFlags(prev => ({ ...prev, threadHistory: false }));
    }, [isNewEmail]);

    // Auto-reset matter-dependent flags if matter is unlinked mid-session
    useEffect(() => {
        if (!hasMatter) {
            setContextFlags(prev => ({ ...prev, matterNotes: false, recentActivity: false, priorComms: false }));
            setAvailableNotes([]);
            setSelectedNotes({});
        }
    }, [hasMatter]);

    const matter = linkedMatter || {};

    // ── Contacts for recipient picker ──
    const [matterContacts, setMatterContacts] = useState([]);
    const [contactsExpanded, setContactsExpanded] = useState(false);
    const [recipientFeedback, setRecipientFeedback] = useState(null);

    // ── Filing Toggle ──
    const [fileOnSend, setFileOnSend] = useState(true);

    // ── AI Section Collapsible ──
    const [aiExpanded, setAiExpanded] = useState(false);

    // If fileOnSend is toggled off, clear the internet header. Otherwise ensure it's there.
    useEffect(() => {
        const matterId = matter?.id || matter?.matter_id;
        import('../services/office').then(({ setMatterInternetHeader }) => {
            if (matterId && fileOnSend && (surface === 'compose' || surface === 'ComposePane')) {
                setMatterInternetHeader(matterId).catch(() => {});
            } else if (!fileOnSend && (surface === 'compose' || surface === 'ComposePane')) {
                setMatterInternetHeader(null).catch(() => {});
            }
        });
    }, [fileOnSend, matter?.id, matter?.matter_id, surface]);

    useEffect(() => {
        const matterId = matter?.id || matter?.matter_id;
        if (!matterId) {
            setMatterContacts([]);
            return;
        }
        let cancelled = false;
        webhook.getMatterDetails({ userEmail, matterId })
            .then(res => {
                if (cancelled) return;
                const contacts = [];
                if (res.matter?.client) {
                    contacts.push({ ...res.matter.client, relationship: 'Client' });
                }
                (res.matter?.contacts || []).forEach(c => contacts.push(c));
                setMatterContacts(contacts);
            })
            .catch(() => setMatterContacts([]));
        return () => { cancelled = true; };
    }, [matter?.id, matter?.matter_id, userEmail]);

    const handleAddRecipient = useCallback(async (email, name, field) => {
        if (!email) return;
        try {
            await addRecipient(email, name, field);
            setRecipientFeedback({ field, name: name || email });
            setTimeout(() => setRecipientFeedback(null), 2500);
        } catch (err) {
            console.error('addRecipient failed:', err);
            alert(`Could not add recipient: ${err.message}`);
        }
    }, []);

    // Fetch notes dynamically when matterNotes is toggled on (and we haven't already fetched)
    useEffect(() => {
        const matterId = matter?.id || matter?.matter_id;
        if (contextFlags.matterNotes && !availableNotes.length && !loadingNotes && !notesError && matterId) {
            setLoadingNotes(true);
            setNotesError(null);
            webhook.getMatterNotes({ userEmail, matterId })
                .then(res => {
                    const notes = res.notes || res.data || [];
                    setAvailableNotes(notes);
                    const initialSelect = {};
                    // All fetched notes checked by default
                    notes.forEach(n => initialSelect[n.id] = true);
                    setSelectedNotes(initialSelect);
                })
                .catch(err => {
                    console.error("Failed to load notes:", err);
                    setNotesError(err.message);
                })
                .finally(() => setLoadingNotes(false));
        }
    }, [contextFlags.matterNotes, matter, userEmail, availableNotes.length, loadingNotes, notesError]);

    // Live token estimate based on current selections
    const savedInstructionsForMeter = userSettings?.instructions || {};
    const toneOverrideForMeter = savedInstructionsForMeter.toneOverrides?.[selectedTone] || '';
    const { estimatedTokens, maxTokens, pct } = useMemo(() => {
        let total = PROMPT_OVERHEAD;
        if (contextFlags.threadHistory) total += CONTEXT_SOURCE_ESTIMATES.threadHistory;
        if (contextFlags.matterNotes) {
            // Estimate based on number of selected notes
            const count = Object.keys(selectedNotes).filter(id => selectedNotes[id]).length;
            total += count * 150;
        }
        if (contextFlags.recentActivity) total += CONTEXT_SOURCE_ESTIMATES.recentActivity;
        if (contextFlags.priorComms) total += CONTEXT_SOURCE_ESTIMATES.priorComms;
        total += estimateTokens(savedInstructionsForMeter.general);
        total += estimateTokens(toneOverrideForMeter);
        total += estimateTokens(customInstructions);
        const max = 10000;
        return { estimatedTokens: total, maxTokens: max, pct: Math.min(100, Math.round((total / max) * 100)) };
    }, [contextFlags, customInstructions, savedInstructionsForMeter.general, toneOverrideForMeter, selectedNotes]);

    const handleModelSelect = (e) => {
        const modelId = e.target.value;
        setSelectedModel(modelId);
        const model = activeModels.find((m) => m.id === modelId);
        if (model) {
            setStatusLabel(`${model.name} · Ready`);
        }
    };

    const toggleContext = (key) => {
        setContextFlags((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const handleGenerate = async () => {
        const matterId = matter?.id || matter?.matter_id;
        if (!matterId) {
            alert('Please link a matter first.');
            return;
        }

        setIsGenerating(true);
        setStatusLabel('Generating draft...');
        setDraftText('');

        try {
            // Retrieve structured instructions
            const savedInstructions = userSettings?.instructions || {};
            const generalInstructions = savedInstructions.general?.trim() || '';
            const toneInstructions = savedInstructions.toneOverrides?.[selectedTone]?.trim() || '';
            const additionalInstructions = customInstructions.trim();

            // Resolve full model object so N8N gets provider + exact API model ID
            const activeModel = activeModels.find(m => m.id === selectedModel) || activeModels[0];

            // Send full note objects for selected notes (or empty array if none)
            const matterNotesArray = contextFlags.matterNotes
                ? availableNotes.filter(n => selectedNotes[n.id])
                : [];

            // Query current compose attachments so the AI can reference them
            const composeAttachments = await getComposeAttachments();
            const attachmentMeta = composeAttachments
                .filter(a => !a.isInline)
                .map(a => ({ name: a.name, size: a.size, contentType: a.contentType }));

            const res = await webhook.draftReply({
                userEmail,
                matterId: matter.id || matter.matter_id,
                matterNumber: matter.display_number || matter.number || matter.matter_number,
                conversationId,
                itemId,
                tone: selectedTone,
                model: selectedModel,
                modelProvider: activeModel.provider,
                modelApiId: activeModel.apiId,
                context_included: contextFlags.threadHistory,
                thread_limit: threadLimit,
                matter_notes: matterNotesArray,
                recent_activity: contextFlags.recentActivity,
                prior_activity: contextFlags.priorComms,
                general_instructions: generalInstructions,
                tone_instructions: toneInstructions,
                additional_instructions: additionalInstructions,
                attachments: attachmentMeta
            });

            if (res.status === 'success' && res.draft) {
                setDraftText(res.draft);
                setStatusLabel('Draft ready');
            } else {
                setDraftText('Error: ' + (res.error || 'Failed to generate draft.'));
                setStatusLabel('Generation failed');
            }
        } catch (err) {
            console.error('Failed to generate draft reply:', err);
            setDraftText(`Error: ${err.message}`);
            setStatusLabel('Generation error');
        } finally {
            setIsGenerating(false);
        }
    };

    const handleInsert = async () => {
        if (!draftText) return;

        try {
            setStatusLabel('Inserting draft...');
            // Convert plain-text newlines to HTML paragraphs for proper formatting
            const htmlDraft = draftText
                .split(/\n\n+/)
                .map(para => `<p>${para.replace(/\n/g, '<br/>')}</p>`)
                .join('');
            await insertDraftBody(htmlDraft);
            setStatusLabel('Draft inserted');
        } catch (err) {
            console.error('Failed to insert draft:', err);
            setStatusLabel('Insert error');
            alert(`Could not insert draft: ${err.message}`);
        }
    };



    return (
        <div className="addin-body">
            <MatterLookup />

            {/* Contacts & Recipient Picker — only shown in compose mode when a matter is linked */}
            {hasMatter && matterContacts.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                    <div
                        className="section-label"
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => setContactsExpanded(p => !p)}
                    >
                        <span>Matter Contacts</span>
                        <span style={{ fontSize: '10px', color: 'var(--grey02)', fontWeight: 400 }}>
                            {contactsExpanded ? '▲ collapse' : `▼ ${matterContacts.length} contact${matterContacts.length !== 1 ? 's' : ''}`}
                        </span>
                    </div>

                    {recipientFeedback && (
                        <div style={{ fontSize: '9px', color: 'var(--teal)', padding: '2px 0 4px', fontWeight: 600 }}>
                            ✓ {recipientFeedback.name} added to {recipientFeedback.field.toUpperCase()}
                        </div>
                    )}

                    {contactsExpanded && (
                        <div className="info-block" style={{ padding: '4px 0' }}>
                            {matterContacts.map((contact, i) => {
                                const emails = contact.email_addresses?.length
                                    ? contact.email_addresses
                                    : contact.email
                                        ? [{ address: typeof contact.email === 'object' ? contact.email.address : contact.email, name: 'Primary' }]
                                        : [];
                                return (
                                    <div key={contact.id || i} style={{ padding: '7px 11px', borderBottom: i < matterContacts.length - 1 ? '1px solid var(--grey04)' : 'none' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '6px' }}>
                                            <div>
                                                <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--navy)' }}>{contact.name}</div>
                                                <div style={{ fontSize: '9px', color: 'var(--grey02)' }}>{contact.relationship || 'Related'}</div>
                                            </div>
                                        </div>
                                        {emails.length === 0 && (
                                            <div style={{ fontSize: '9px', color: 'var(--grey03)', marginTop: '4px', fontStyle: 'italic' }}>No email address</div>
                                        )}
                                        {emails.map((em, ei) => (
                                            <div key={ei} style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '5px', flexWrap: 'wrap' }}>
                                                <span style={{ fontSize: '10px', color: 'var(--grey02)', flex: 1, minWidth: 0 }} className="truncate">{em.address}</span>
                                                {['to', 'cc', 'bcc'].map(field => (
                                                    <button
                                                        key={field}
                                                        onClick={() => handleAddRecipient(em.address, contact.name, field)}
                                                        style={{
                                                            padding: '2px 6px', fontSize: '9px', fontWeight: 700,
                                                            background: field === 'to' ? 'var(--teal)' : field === 'cc' ? 'var(--navy)' : 'var(--grey03)',
                                                            color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer',
                                                            textTransform: 'uppercase', letterSpacing: '0.3px', flexShrink: 0
                                                        }}
                                                    >
                                                        {field}
                                                    </button>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* Auto-file on send toggle */}
            {hasMatter && (
                <div style={{ marginTop: '8px', marginBottom: '16px', background: '#e0f2f1', padding: '10px 12px', borderRadius: '6px', border: '1px solid #b2dfdb', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }} onClick={() => setFileOnSend(!fileOnSend)}>
                    <input type="checkbox" checked={fileOnSend} readOnly style={{ accentColor: 'var(--teal)', cursor: 'pointer', transform: 'scale(1.1)' }} />
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--navy)' }}>Log to Clio on Send</span>
                        <span style={{ fontSize: '9px', color: 'var(--text-secondary)', marginTop: '2px' }}>Automatically files this conversation to {matter.number || matter.matter_number}</span>
                    </div>
                </div>
            )}

            {/* AI Generation Section — Collapsible */}
            <div
                className="section-label"
                style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    cursor: 'pointer', userSelect: 'none', borderTop: '1px solid var(--border)', paddingTop: '16px', marginTop: '16px'
                }}
                onClick={() => setAiExpanded(!aiExpanded)}
            >
                <div>
                    <span style={{ color: 'var(--orange)' }}>✨</span> AI Draft Composer
                </div>
                <span style={{ fontSize: '10px', color: 'var(--grey02)', fontWeight: 400 }}>
                    {aiExpanded ? '▲ collapse' : '▼ expand'}
                </span>
            </div>

            {aiExpanded && (
                <>
                    {/* Model picker */}
                    <div className="section-label" style={{ marginTop: '12px' }}>AI Model</div>
            <div style={{ marginBottom: '16px' }}>
                <select
                    className="form-input"
                    value={selectedModel}
                    onChange={handleModelSelect}
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--border)', fontSize: '13px' }}
                >
                    {activeModels.map((model) => (
                        <option key={model.id} value={model.id}>
                            {model.name} ({model.provider})
                        </option>
                    ))}
                </select>
                {activeModels.find(m => m.id === selectedModel)?.desc && (
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                        {activeModels.find(m => m.id === selectedModel).desc}
                    </div>
                )}
            </div>

            {/* Tone selector */}
            <div className="section-label">Tone</div>
            <div className="tone-row">
                {TONES.map((tone) => (
                    <button
                        key={tone}
                        className={`tone-pill${selectedTone === tone ? ' active' : ''}`}
                        onClick={() => setSelectedTone(tone)}
                        id={`tone-${tone.toLowerCase()}`}
                    >
                        {tone}
                    </button>
                ))}
            </div>

            {/* Context injection — only shows options relevant to this email */}
            {(!isNewEmail || hasMatter) && (
                <div className="section-label">Context Injected</div>
            )}

            {/* Thread history — only available when replying to an existing thread */}
            {!isNewEmail && (
                <>
                    <div className="context-row" onClick={() => toggleContext('threadHistory')}>
                        <div className={`ctx-check${contextFlags.threadHistory ? '' : ' off'}`} />
                        <div className="ctx-label">Thread history (default)</div>
                        <div className="ctx-badge">Auto</div>
                    </div>
                    {contextFlags.threadHistory && (
                        <div style={{ paddingLeft: '28px', paddingBottom: '8px', fontSize: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input
                                    type="range"
                                    min="2"
                                    max="10"
                                    value={threadLimit}
                                    onChange={(e) => setThreadLimit(parseInt(e.target.value, 10))}
                                    style={{ flex: 1, cursor: 'pointer' }}
                                />
                                <span style={{ color: 'var(--text-secondary)' }}>Include {threadLimit} emails</span>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Matter-dependent context — only shown when a matter is linked */}
            {hasMatter && (
                <>
                    <div className="context-row" onClick={() => toggleContext('matterNotes')}>
                        <div className={`ctx-check${contextFlags.matterNotes ? '' : ' off'}`} />
                        <div className="ctx-label">Matter Notes</div>
                        <div className="ctx-badge">Auto</div>
                    </div>
                    {contextFlags.matterNotes && (
                        <div style={{ paddingLeft: '28px', paddingBottom: '8px', fontSize: '12px' }}>
                            {loadingNotes && <div style={{ color: 'var(--text-secondary)' }}>Loading notes...</div>}
                            {notesError && <div style={{ color: 'var(--orange)' }}>Failed to load notes.</div>}
                            {!loadingNotes && availableNotes.length === 0 && !notesError && (
                                <div style={{ color: 'var(--text-secondary)' }}>No notes found for this matter.</div>
                            )}
                            {!loadingNotes && availableNotes.length > 0 && availableNotes.map((note) => (
                                <div key={note.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '8px', cursor: 'pointer', padding: '4px', borderRadius: '4px', border: '1px solid transparent' }} onClick={() => setSelectedNotes(prev => ({ ...prev, [note.id]: !prev[note.id] }))}>
                                    <input type="checkbox" checked={!!selectedNotes[note.id]} readOnly style={{ marginTop: '2px', cursor: 'pointer' }} />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ color: 'var(--navy)', fontWeight: 500, lineHeight: 1.2 }}>
                                            {note.subject || note.detail || `Note ${note.id}`}
                                        </div>
                                        <div style={{ color: '#8a8a8a', fontSize: '10px', marginTop: '2px' }}>
                                            Updated: {note.updated_at ? new Date(note.updated_at).toLocaleDateString() : 'Unknown'}
                                        </div>
                                    </div>
                                    <button
                                        className="action-btn"
                                        style={{ padding: '2px 6px', fontSize: '10px', height: 'auto', alignSelf: 'flex-start' }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            const matterIdStr = typeof note.matter === 'object' ? note.matter.id : (note.matter || '');
                                            if (matterIdStr) {
                                                window.open(`https://app.clio.com/nc/#/matters/${matterIdStr}/notes`, '_blank');
                                            } else if (matter?.id || matter?.matter_id) {
                                                const mId = matter.id || matter.matter_id;
                                                window.open(`https://app.clio.com/nc/#/matters/${mId}/notes`, '_blank');
                                            }
                                        }}
                                    >
                                        Open in Clio
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="context-row" onClick={() => toggleContext('recentActivity')}>
                        <div className={`ctx-check${contextFlags.recentActivity ? '' : ' off'}`} />
                        <div className="ctx-label">Recent Activity</div>
                        <div className="ctx-badge">Auto</div>
                    </div>

                    <div className="context-row" onClick={() => toggleContext('priorComms')}>
                        <div className={`ctx-check${contextFlags.priorComms ? '' : ' off'}`} />
                        <div className="ctx-label">Prior communications log</div>
                        <div className="ctx-badge" style={{ background: '#f5f0e8', color: '#8a6a20' }}>Manual</div>
                    </div>
                </>
            )}

            {/* Hint when context section is completely empty */}
            {isNewEmail && !hasMatter && (
                <div style={{ fontSize: '10px', color: 'var(--grey02)', padding: '6px 0 4px', fontStyle: 'italic' }}>
                    Link a matter above to unlock context options.
                </div>
            )}

            {/* Custom Instructions */}
            <div className="section-label" style={{ marginTop: '16px' }}>Additional Instructions</div>
            <textarea
                className="form-input"
                style={{ width: '100%', minHeight: '60px', borderRadius: '6px', padding: '8px', fontSize: '13px', resize: 'vertical' }}
                placeholder="e.g. Please emphasize the indemnity clause..."
                value={customInstructions}
                onChange={(e) => setCustomInstructions(e.target.value)}
            />

            {/* Token meter */}
            <div className="token-meter">
                <div className="token-label">Context</div>
                <div className="token-bar-track">
                    <div className="token-bar-fill" style={{
                        width: `${pct}%`,
                        background: pct > 85 ? 'var(--orange)' : undefined,
                    }} />
                </div>
                <div className="token-label">~{estimatedTokens.toLocaleString()} / {(maxTokens / 1000).toFixed(0)}k tokens</div>
            </div>

            {/* Generated draft (Hidden unless generated/generating) */}
            {(draftText || isGenerating) && (
                <>
                    <div className="section-label">Generated Draft</div>
                    {isGenerating ? (
                        <div className="draft-area">
                            <div className="draft-generating">
                                <div className="gen-dots">
                                    <div className="gen-dot" />
                                    <div className="gen-dot" />
                                    <div className="gen-dot" />
                                </div>
                                <div className="gen-label">Generating with {activeModels.find((m) => m.id === selectedModel)?.name}…</div>
                            </div>
                        </div>
                    ) : (
                        <div className="draft-area">
                            {draftText.split('\n').map((line, i) => (
                                <React.Fragment key={i}>
                                    {line}
                                    {i < draftText.split('\n').length - 1 && <br />}
                                </React.Fragment>
                            ))}
                            <span className="draft-cursor" />
                        </div>
                    )}
                </>
            )}

            {/* Draft action buttons */}
            <div className="draft-btn-row">
                {!draftText && !isGenerating ? (
                    <button className="draft-btn primary" style={{ width: '100%' }} id="generate-btn" onClick={handleGenerate}>
                        Generate Draft ✨
                    </button>
                ) : (
                    <>
                        <button className="draft-btn secondary" id="regen-btn" onClick={handleGenerate} disabled={isGenerating}>
                            ↺ Regen
                        </button>
                        <button className="draft-btn secondary" id="refine-btn" disabled={isGenerating || !draftText}>
                            ✎ Refine
                        </button>
                        <button className="draft-btn primary" id="insert-btn" onClick={handleInsert} disabled={isGenerating || !draftText}>
                            Insert →
                        </button>
                    </>
                )}
            </div>
            </>
            )}

        </div>
    );
}
